import React, { useState, useMemo } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { HiChevronDown, HiOutlineTrash, HiPlus } from 'react-icons/hi2';
import RaptorDatePicker from '../../../../selects/RaptorDatePicker.component';
import dayjs, { Dayjs } from 'dayjs';
import LoadingSpinner from '../../../../ui/LoadingSpinner';
import GenericModal from '../../../../ui/GenericModal';
import { Button } from '@mui/material';
import { getAllRelevantWeekdays } from '../../../../../utilities/dateUtilities';
import {
  buildRangeQueryKey,
  fetchCustomDateRangeData,
  useCustomDateRangeQuery,
} from '../../queries';
import { RegulationBreakdownData } from '../../types/dashboard.types';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import {
  findInitialStartDate,
  getInclusiveDays,
  isStandardDateRange,
} from '../../utils/date.utils';
import { useSelector } from 'react-redux';
import { raptorPlatformIdSelector } from '../../../../../redux/raptorPlatform/selectors';

interface CustomDateRange {
  id: string;
  startDate: string;
  endDate: string;
}

interface CustomDateRangeSelectorProps {
  onChange?: (value: { startDate: string; endDate: string }) => void;
  datesToInclude?: string[];
  isSelected?: boolean;
  clearSelection?: boolean;
  activeCustomRange?: { startDate: string; endDate: string } | null;
}

const CustomDateRangeSelector: React.FC<CustomDateRangeSelectorProps> = ({
  onChange,
  datesToInclude = getAllRelevantWeekdays(),
  isSelected = false,
  activeCustomRange = null,
}) => {
  const queryClient = useQueryClient();
  const initialStartDate = findInitialStartDate(datesToInclude);
  const raptorPlatformId = useSelector(raptorPlatformIdSelector);

  const cache = queryClient.getQueryCache().getAll();

  // UI state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newRangeStartDate, setNewRangeStartDate] =
    useState<Dayjs>(initialStartDate);
  const [newRangeEndDate, setNewRangeEndDate] = useState<Dayjs>(dayjs());
  const [selectedCustomRange, setSelectedCustomRange] =
    useState<CustomDateRange | null>(null);

  // Track initiated custom ranges (including pending ones)
  const [initiatedRanges, setInitiatedRanges] = useState<Set<string>>(
    new Set(),
  );

  // Derive custom ranges from both cache and initiated ranges
  const customRanges = useMemo(() => {
    const rangeMap = new Map<string, CustomDateRange>();

    // Add ranges from cache (completed or in-progress queries)
    const customRangeQueries = cache.filter((query) => {
      const key = query.queryKey;
      return (
        Array.isArray(key) &&
        key[0] === 'dashboard-data-v2' &&
        key[1] && // startDate
        key[2] && // endDate
        // Filter out standard ranges (Current, 7, 30, 90 days)
        !isStandardDateRange(key[1] as string, key[2] as string)
      );
    });

    customRangeQueries.forEach((query) => {
      const [, startDate, endDate] = query.queryKey as [
        string,
        string,
        string,
        any,
      ];
      const rangeKey = `${startDate}-${endDate}`;
      rangeMap.set(rangeKey, {
        id: rangeKey,
        startDate,
        endDate,
      });
    });

    // Add initiated ranges that might not be in cache yet
    initiatedRanges.forEach((rangeKey) => {
      if (!rangeMap.has(rangeKey)) {
        const [startDate, endDate] = rangeKey.split('-');
        if (startDate && endDate && !isStandardDateRange(startDate, endDate)) {
          rangeMap.set(rangeKey, {
            id: rangeKey,
            startDate,
            endDate,
          });
        }
      }
    });

    return Array.from(rangeMap.values());
  }, [cache, initiatedRanges]);

  // Use the custom date range query for caching and loading states
  const {
    isLoading: isQueryLoading,
    isFetching: isQueryFetching,
    error: queryError,
  } = useCustomDateRangeQuery(
    selectedCustomRange?.startDate || null,
    selectedCustomRange?.endDate || null,
    raptorPlatformId,
  );

  // Subscribe to every custom range's query so the dropdown reactively
  // reflects loading state even for ranges that aren't the active one.
  const rangeQueryResults = useQueries({
    queries: customRanges.map((range) => ({
      queryKey: buildRangeQueryKey(
        range.startDate,
        range.endDate,
        raptorPlatformId,
      ),
      queryFn: () =>
        fetchCustomDateRangeData(
          range.startDate,
          range.endDate,
          raptorPlatformId,
        ),
      staleTime: 60 * 60 * 1000,
    })),
  });

  const loadingByRangeId = useMemo(() => {
    const map: Record<string, boolean> = {};
    customRanges.forEach((range, idx) => {
      const result = rangeQueryResults[idx];
      map[range.id] = !!result && (result.isLoading || result.isFetching);
    });
    return map;
  }, [customRanges, rangeQueryResults]);

  // Filter selectable dates so start <= end and end >= start
  const startDatesToInclude = React.useMemo(
    () =>
      datesToInclude.filter((d) => !dayjs(d).isAfter(newRangeEndDate, 'day')),
    [datesToInclude, newRangeEndDate],
  );

  const endDatesToInclude = React.useMemo(
    () =>
      datesToInclude.filter(
        (d) => !dayjs(d).isBefore(newRangeStartDate, 'day'),
      ),
    [datesToInclude, newRangeStartDate],
  );

  // Clear selection when custom is no longer selected
  React.useEffect(() => {
    if (!isSelected) {
      setSelectedCustomRange(null);
    }
  }, [isSelected]);

  // Function to reset form values
  const resetForm = () => {
    const newInitialStartDate = findInitialStartDate(datesToInclude);
    setNewRangeStartDate(newInitialStartDate);
    setNewRangeEndDate(dayjs());
  };

  // Function to open modal and reset values
  const handleOpenModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const handleCreateRange = () => {
    const newStartDate = newRangeStartDate.format('YYYY-MM-DD');
    const newEndDate = newRangeEndDate.format('YYYY-MM-DD');
    const rangeKey = `${newStartDate}-${newEndDate}`;

    // Add to initiated ranges immediately
    setInitiatedRanges((prev) => new Set([...prev, rangeKey]));

    // Create range object for selection
    const newRange: CustomDateRange = {
      id: rangeKey,
      startDate: newStartDate,
      endDate: newEndDate,
    };

    // Select the range - this will trigger the query and add it to cache
    setSelectedCustomRange(newRange);
    onChange?.({ startDate: newStartDate, endDate: newEndDate });

    // Reset form and close modal
    resetForm();
    setShowCreateModal(false);
  };

  const handleRangeSelect = (range: CustomDateRange) => {
    // Select the range - query will handle loading state
    setSelectedCustomRange(range);
    onChange?.({ startDate: range.startDate, endDate: range.endDate });
  };

  const handleDeleteRange = (range: CustomDateRange) => {
    const rangeKey = `${range.startDate}-${range.endDate}`;

    // Remove from initiated ranges
    setInitiatedRanges((prev) => {
      const newSet = new Set(prev);
      newSet.delete(rangeKey);
      return newSet;
    });

    // Clear the query from cache
    queryClient.removeQueries({
      queryKey: ['dashboard-data-v2', range.startDate, range.endDate],
    });

    // Clear selection if this was the selected range
    if (selectedCustomRange?.id === range.id) {
      setSelectedCustomRange(null);
    }
  };

  // Handle query errors - clear selection on error
  React.useEffect(() => {
    if (queryError && selectedCustomRange) {
      console.error('Failed to fetch data for custom range:', queryError);
      const rangeKey = `${selectedCustomRange.startDate}-${selectedCustomRange.endDate}`;

      // Remove from initiated ranges
      setInitiatedRanges((prev) => {
        const newSet = new Set(prev);
        newSet.delete(rangeKey);
        return newSet;
      });

      // Clear the failed query from cache
      queryClient.removeQueries({
        queryKey: [
          'dashboard-data-v2',
          selectedCustomRange.startDate,
          selectedCustomRange.endDate,
        ],
      });
      setSelectedCustomRange(null);
    }
  }, [queryError, selectedCustomRange, queryClient]);

  return (
    <>
      <div className="relative">
        <Menu>
          <MenuButton
            className={`flex items-center justify-center gap-1.5 rounded-md px-3 py-1 text-xs font-medium shadow-sm ring-1 ring-gray-200/50 focus:outline-none focus:ring-2 focus:ring-mainblue/20 ${
              isSelected
                ? 'bg-mainblue text-white'
                : 'bg-gray-50/50 text-gray-700 hover:bg-gray-100'
            }`}
          >
            <span>Custom</span>
            <HiChevronDown className="h-3 w-3" />
          </MenuButton>

          <MenuItems
            transition
            anchor="bottom end"
            className="mt-2 w-64 origin-bottom-right rounded-md border border-black/5 bg-white p-1 text-sm/6 text-black shadow-lg transition duration-100 ease-out focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0"
            style={{ zIndex: 1000 }}
          >
            {/* Header with Add button */}
            <div className="mb-1 flex items-center justify-between px-2 py-1">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Custom Ranges
              </span>
              <button
                onClick={handleOpenModal}
                className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-mainblue text-white transition-colors duration-200 hover:bg-mainblue/90"
                title="Add new custom range"
              >
                <HiPlus className="h-3 w-3" />
              </button>
            </div>

            {/* Existing custom ranges */}
            {customRanges.length > 0 ? (
              <>
                <div className="border-t border-gray-100" />
                {customRanges.map((range) => {
                  const isActiveRange =
                    !!activeCustomRange &&
                    activeCustomRange.startDate === range.startDate &&
                    activeCustomRange.endDate === range.endDate;

                  const showLoading =
                    loadingByRangeId[range.id] ||
                    (isActiveRange && (isQueryLoading || isQueryFetching));
                  const itemClasses = `group flex w-full items-center justify-between rounded-md px-3 py-2 text-left disabled:cursor-not-allowed disabled:opacity-50 ${
                    isActiveRange
                      ? 'bg-blue-100 text-blue-900'
                      : 'hover:bg-blue-50'
                  }`;

                  return (
                    <MenuItem key={range.id}>
                      <button
                        onClick={() => handleRangeSelect(range)}
                        className={itemClasses}
                      >
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900">
                            {getInclusiveDays(range.startDate, range.endDate)}{' '}
                            days
                          </span>
                          <span className="text-xs text-gray-500">
                            {dayjs(range.startDate).format('MMM DD, YYYY')} -{' '}
                            {dayjs(range.endDate).format('MMM DD, YYYY')}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {showLoading && (
                            <LoadingSpinner className="h-4 w-4 border-2" />
                          )}
                          {!isActiveRange && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteRange(range);
                              }}
                              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                              title="Delete range"
                            >
                              <HiOutlineTrash className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </button>
                    </MenuItem>
                  );
                })}
              </>
            ) : (
              <div className="px-3 py-4 text-center">
                <p className="text-xs text-gray-400">No custom ranges yet</p>
                <p className="mt-1 text-xs text-gray-300">
                  Click + to create one
                </p>
              </div>
            )}
          </MenuItems>
        </Menu>
      </div>

      {/* Generic Modal with Custom Content */}
      <GenericModal
        isOpen={showCreateModal}
        setIsOpen={setShowCreateModal}
        className="max-w-xl"
        title="Create Custom Range"
      >
        <div className="space-y-4">
          {/* Date Range */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <RaptorDatePicker
                title="Start Date:"
                handler={(date: Date | null) => {
                  if (date) {
                    const nextStart = dayjs(date);
                    setNewRangeStartDate(nextStart);
                    if (newRangeEndDate.isBefore(nextStart, 'day')) {
                      setNewRangeEndDate(nextStart);
                    }
                  }
                }}
                datesToInclude={startDatesToInclude}
                selectedDate={newRangeStartDate}
                tiedToActiveDate={false}
              />
            </div>
            <div>
              <RaptorDatePicker
                title="End Date:"
                handler={(date: Date | null) => {
                  if (date) {
                    const nextEnd = dayjs(date);
                    if (nextEnd.isBefore(newRangeStartDate, 'day')) {
                      return;
                    }
                    setNewRangeEndDate(nextEnd);
                  }
                }}
                datesToInclude={endDatesToInclude}
                selectedDate={newRangeEndDate}
                tiedToActiveDate={false}
              />
            </div>
          </div>

          {/* Preview - day count */}
          <div className="rounded-md bg-gray-50 p-3">
            <div className="text-sm text-gray-600">
              {dayjs(newRangeEndDate).diff(dayjs(newRangeStartDate), 'days') +
                1}{' '}
              days
            </div>
            <div className="mt-1 text-xs text-gray-500">
              {dayjs(newRangeStartDate).format('MMM DD, YYYY')} -{' '}
              {dayjs(newRangeEndDate).format('MMM DD, YYYY')}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4">
          <Button
            variant="outlined"
            onClick={() => setShowCreateModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateRange}
            className="bg-mainblue hover:bg-mainblue/90"
          >
            Create Range
          </Button>
        </div>
      </GenericModal>
    </>
  );
};

export default CustomDateRangeSelector;
